import { useState, useEffect, useRef } from "react";
import api, { API, getToken } from "../api";
import { useConfirm } from "./ConfirmDialog";
import { AttachmentList, AttachmentComposer } from "./Attachments";

export default function Workstream({ ws, idx, isAdmin, onChange, state, refresh, forceOpen }) {
  const confirm = useConfirm();
  const [open, setOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState(ws.note);
  const [linkDraft, setLinkDraft] = useState(ws.deliverable?.url || "");
  const [cmtDraft, setCmtDraft] = useState("");
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingLinks, setPendingLinks] = useState([]);
  const [linkInput, setLinkInput] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const MAX_LINKS = 5;

  useEffect(() => {
    if (forceOpen) setOpen(true);
  }, [forceOpen]);

  const addPendingLink = () => {
    const raw = linkInput.trim();
    if (!raw) return;
    let url = raw;
    if (!/^https?:\/\//i.test(url)) url = "https://" + url;
    if (pendingLinks.includes(url)) {
      setLinkInput("");
      return;
    }
    if (pendingLinks.length >= MAX_LINKS) return;
    setPendingLinks([...pendingLinks, url]);
    setLinkInput("");
  };

  const removePendingLink = (i) => {
    setPendingLinks(pendingLinks.filter((_, j) => j !== i));
  };

  const d = ws.deliverable || { kind: "link", url: "", fileName: "", storagePath: "", updatedAt: "" };
  const cmts = ws.comments || [];
  const newCount = cmts.filter((c) => c.state === "new").length;
  const progress =
    typeof ws.progress === "number"
      ? ws.progress
      : ws.status === "done"
      ? 100
      : ws.status === "progress"
      ? 50
      : 0;
  const progressStatus = progress >= 100 ? "done" : progress <= 0 ? "pending" : "progress";
  const pillText =
    progress >= 100 ? "Done" : progress <= 0 ? "Not Started" : `${progress}% · In Progress`;

  // ----- helpers to update local state -----
  const replaceWs = (updated) => {
    const next = { ...state };
    next.workstreams = state.workstreams.map((x, i) => (i === idx ? updated : x));
    onChange(next);
  };

  // ----- mutations -----
  const setProgress = async (p) => {
    const { data } = await api.patch(`/workstreams/${ws.n}`, { progress: p });
    replaceWs({ ...ws, ...data });
  };

  const saveNote = async () => {
    if (noteDraft === ws.note) return;
    const { data } = await api.patch(`/workstreams/${ws.n}`, { note: noteDraft });
    replaceWs({ ...ws, ...data });
  };

  const setDelivKind = async (kind) => {
    const { data } = await api.put(`/workstreams/${ws.n}/deliverable`, { kind, url: linkDraft });
    replaceWs({ ...ws, deliverable: data });
  };

  const saveLink = async () => {
    const { data } = await api.put(`/workstreams/${ws.n}/deliverable`, {
      kind: "link",
      url: linkDraft,
    });
    replaceWs({ ...ws, deliverable: data });
  };

  const onUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", f);
      const { data } = await api.post(
        `/workstreams/${ws.n}/deliverable/upload`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      replaceWs({ ...ws, deliverable: data });
    } catch (err) {
      alert("Upload failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addComment = async () => {
    const body = cmtDraft.trim();
    if (!body && pendingFiles.length === 0 && pendingLinks.length === 0) return;
    const { data } = await api.post(`/workstreams/${ws.n}/comments`, {
      body: body || (pendingLinks.length ? "(link)" : "(attachment)"),
      links: pendingLinks,
    });
    let newCmt = data;
    if (pendingFiles.length > 0) {
      const newCi = cmts.length; // index of the just-posted comment
      const uploaded = [];
      for (const f of pendingFiles) {
        const form = new FormData();
        form.append("file", f);
        try {
          const res = await api.post(
            `/workstreams/${ws.n}/comments/${newCi}/attachments`,
            form,
            { headers: { "Content-Type": "multipart/form-data" } }
          );
          uploaded.push(res.data);
        } catch (err) {
          alert(`Attachment "${f.name}" failed: ` + (err.response?.data?.detail || err.message));
        }
      }
      newCmt = { ...newCmt, attachments: uploaded };
    }
    const next = { ...state };
    next.workstreams = state.workstreams.map((x, i) =>
      i === idx ? { ...ws, comments: [...cmts, newCmt] } : x
    );
    onChange(next);
    setCmtDraft("");
    setPendingFiles([]);
    setPendingLinks([]);
    setLinkInput("");
  };

  const delCommentAttachment = async (ci, ai) => {
    await api.delete(`/workstreams/${ws.n}/comments/${ci}/attachments/${ai}`);
    const next = { ...state };
    next.workstreams = state.workstreams.map((x, i) =>
      i === idx
        ? {
            ...ws,
            comments: cmts.map((c, j) =>
              j === ci ? { ...c, attachments: (c.attachments || []).filter((_, k) => k !== ai) } : c
            ),
          }
        : x
    );
    onChange(next);
  };

  const setCmtState = async (ci, st) => {
    const { data } = await api.patch(`/workstreams/${ws.n}/comments/${ci}`, { state: st });
    const next = { ...state };
    next.workstreams = state.workstreams.map((x, i) =>
      i === idx
        ? {
            ...ws,
            comments: cmts.map((c, j) => (j === ci ? { ...c, ...data } : c)),
          }
        : x
    );
    onChange(next);
  };

  const delCmt = async (ci) => {
    const c = cmts[ci];
    const preview = (c?.body || "").slice(0, 120);
    const ok = await confirm({
      title: "Delete this comment?",
      message: `"${preview}${(c?.body || "").length > 120 ? "…" : ""}"\n\nThis cannot be undone.`,
      confirmLabel: "Delete",
    });
    if (!ok) return;
    await api.delete(`/workstreams/${ws.n}/comments/${ci}`);
    const next = { ...state };
    next.workstreams = state.workstreams.map((x, i) =>
      i === idx ? { ...ws, comments: cmts.filter((_, j) => j !== ci) } : x
    );
    onChange(next);
  };

  // ----- file viewer helpers -----
  const fileSrc = d.storagePath
    ? `${API}/files?path=${encodeURIComponent(d.storagePath)}&token=${encodeURIComponent(getToken() || "")}`
    : "";

  const downloadFile = async () => {
    if (!d.storagePath) return;
    const res = await api.get(`/files?path=${encodeURIComponent(d.storagePath)}`, {
      responseType: "blob",
    });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = d.fileName || "deliverable";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  };

  // ----- deliverable view block -----
  let delivView;
  if (d.kind === "link" && d.url) {
    delivView = (
      <div className="ws-link-row">
        <a
          className="ws-link"
          href={d.url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`ws-${ws.n}-view-link`}
        >
          🔗 View completed deliverable
        </a>
        <div className="deliv-meta">
          External link{d.updatedAt ? ` · updated ${d.updatedAt}` : ""}
        </div>
      </div>
    );
  } else if (d.kind === "file" && d.fileName) {
    delivView = (
      <div className="ws-link-row">
        {d.storagePath ? (
          <>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
              <span
                className="ws-link"
                style={{ background: "var(--bg-input)", borderColor: "var(--line)", color: "var(--text-dim)", cursor: "default" }}
              >
                📄 {d.fileName}
              </span>
              <button
                className="ws-link"
                style={{ background: "var(--bg-input)", borderColor: "var(--line)", color: "var(--text-dim)" }}
                onClick={downloadFile}
                data-testid={`ws-${ws.n}-download-file`}
              >
                ⬇ Download
              </button>
              <a
                className="ws-link"
                href={fileSrc}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`ws-${ws.n}-open-tab`}
              >
                ↗ Open in new tab
              </a>
            </div>
            <div className="deliv-meta">
              Uploaded file{d.updatedAt ? ` · ${d.updatedAt}` : ""}
            </div>
            <iframe
              className="deliv-frame"
              src={fileSrc}
              title={d.fileName}
              data-testid={`ws-${ws.n}-preview-iframe`}
            />
          </>
        ) : (
          <>
            <span
              className="ws-link"
              style={{ background: "var(--bg-input)", borderColor: "var(--line)", color: "var(--text-dim)", cursor: "default" }}
            >
              📄 {d.fileName}
            </span>
            <div className="deliv-meta">
              Sample reference — re-upload the file to enable inline preview.
            </div>
          </>
        )}
      </div>
    );
  } else {
    delivView = (
      <div className="ws-link-row">
        <div className="deliv-empty">No deliverable attached yet.</div>
      </div>
    );
  }

  return (
    <div className="ws" data-testid={`ws-${ws.n}`}>
      <div className="ws-top" onClick={() => setOpen((v) => !v)}>
        <span className="ws-num">{String(ws.n).padStart(2, "0")}</span>
        <span className="ws-title">{ws.title}</span>
        {cmts.length > 0 && (
          <span
            className={`cmt-count ${newCount ? "has-new" : ""}`}
            data-testid={`ws-${ws.n}-cmt-badge`}
          >
            {newCount ? `${newCount} new · ` : ""}{cmts.length} comment{cmts.length > 1 ? "s" : ""}
          </span>
        )}
        <span className="ws-est">{ws.est}</span>
        <span className={`pill ${progressStatus}`} data-testid={`ws-${ws.n}-status-pill`}>{pillText}</span>
      </div>
      {open && (
        <div className="ws-body">
          <p>{ws.desc}</p>
          <div className="label">Current status</div>
          <div className="ws-note">{ws.note}</div>
          <div className="label">Deliverable</div>
          {delivView}

          {isAdmin && (
            <>
              <textarea
                className="note-edit"
                value={noteDraft}
                onChange={(e) => setNoteDraft(e.target.value)}
                onBlur={saveNote}
                data-testid={`ws-${ws.n}-note-edit`}
              />
              <div className="label">Attach deliverable</div>
              <div className="seg">
                <button
                  className={d.kind === "link" ? "on" : ""}
                  onClick={() => setDelivKind("link")}
                  data-testid={`ws-${ws.n}-seg-link`}
                >
                  Link
                </button>
                <button
                  className={d.kind === "file" ? "on" : ""}
                  onClick={() => setDelivKind("file")}
                  data-testid={`ws-${ws.n}-seg-file`}
                >
                  Upload file
                </button>
              </div>
              {d.kind === "link" ? (
                <input
                  className="link-input"
                  placeholder="https://..."
                  value={linkDraft}
                  onChange={(e) => setLinkDraft(e.target.value)}
                  onBlur={saveLink}
                  data-testid={`ws-${ws.n}-link-input`}
                />
              ) : (
                <>
                  <div
                    className="file-drop"
                    onClick={() => fileRef.current?.click()}
                    data-testid={`ws-${ws.n}-file-drop`}
                  >
                    <span>
                      {uploading
                        ? "Uploading…"
                        : d.fileName
                        ? `Current: ${d.fileName} — click to replace`
                        : "Click to choose a file (PDF, doc, image)"}
                    </span>
                  </div>
                  <input
                    type="file"
                    ref={fileRef}
                    style={{ display: "none" }}
                    onChange={onUpload}
                    data-testid={`ws-${ws.n}-file-input`}
                  />
                </>
              )}
              <div className="progress-row" data-testid={`ws-${ws.n}-progress-row`}>
                <div className="progress-row-head">
                  <span className="label" style={{ margin: 0 }}>Progress</span>
                  <span
                    className={`progress-value progress-value-${progressStatus}`}
                    data-testid={`ws-${ws.n}-percent`}
                  >
                    {progress}% · {progress >= 100 ? "Done" : progress <= 0 ? "Not Started" : "In Progress"}
                  </span>
                </div>
                <input
                  type="range"
                  className={`progress-slider progress-slider-${progressStatus}`}
                  min={0}
                  max={100}
                  step={10}
                  value={progress}
                  onChange={(e) => setProgress(Number(e.target.value))}
                  data-testid={`ws-${ws.n}-progress-slider`}
                  aria-label={`Workstream ${ws.n} progress`}
                />
                <div className="progress-ticks" aria-hidden="true">
                  {[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100].map((t) => (
                    <span key={t}>{t === 0 || t === 50 || t === 100 ? `${t}%` : ""}</span>
                  ))}
                </div>
              </div>
            </>
          )}

          <div className="cmt-section">
            <div className="cmt-head">
              <span className="label" style={{ margin: 0 }}>
                Feedback &amp; Comments
              </span>
              <span className={`cmt-count ${newCount ? "has-new" : ""}`}>
                {newCount} new · {cmts.length} total
              </span>
            </div>

            <div className="cmt-add cmt-add-sticky">
              <textarea
                placeholder="Leave feedback on this deliverable..."
                value={cmtDraft}
                onChange={(e) => setCmtDraft(e.target.value)}
                data-testid={`ws-${ws.n}-cmt-input`}
              />
              <AttachmentComposer
                pending={pendingFiles}
                setPending={setPendingFiles}
                testIdPrefix={`ws-${ws.n}-cmt-att`}
              />
              <div className="link-composer" data-testid={`ws-${ws.n}-cmt-link-composer`}>
                {pendingLinks.length > 0 && (
                  <div className="att-pending" style={{ marginBottom: 6 }}>
                    {pendingLinks.map((l, i) => (
                      <span className="att-chip att-chip-pending" key={i}>
                        <span>🔗</span>
                        <span className="att-name">{l}</span>
                        <button
                          type="button"
                          onClick={() => removePendingLink(i)}
                          title="Remove"
                          data-testid={`ws-${ws.n}-cmt-pending-link-${i}-remove`}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {pendingLinks.length < MAX_LINKS && (
                  <div className="link-add-row">
                    <input
                      className="link-add-input"
                      type="url"
                      placeholder={`Add link ${pendingLinks.length + 1} of ${MAX_LINKS} (https://…)`}
                      value={linkInput}
                      onChange={(e) => setLinkInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          addPendingLink();
                        }
                      }}
                      data-testid={`ws-${ws.n}-cmt-link-input`}
                    />
                    <button
                      type="button"
                      className="att-clip"
                      onClick={addPendingLink}
                      data-testid={`ws-${ws.n}-cmt-link-add`}
                    >
                      🔗 Add link
                    </button>
                  </div>
                )}
                {pendingLinks.length >= MAX_LINKS && (
                  <div className="deliv-meta">Maximum of {MAX_LINKS} links per comment reached.</div>
                )}
              </div>
              <button
                className="btn btn-sm"
                onClick={addComment}
                data-testid={`ws-${ws.n}-cmt-post`}
              >
                Post as {isAdmin ? "Marco" : "Ward"}
              </button>
            </div>

            {cmts.length === 0 ? (
              <div className="deliv-empty">No comments yet.</div>
            ) : (
              cmts.map((c, ci) => (
                <div
                  className={`cmt st-${c.state}`}
                  key={c.id || ci}
                  data-testid={`ws-${ws.n}-cmt-${ci}`}
                >
                  <div className="cmt-top">
                    <span className={`cmt-author by-${c.author}`}>
                      {c.author === "WH" ? "Ward" : "Marco"}
                    </span>
                    <span className="cmt-when">{c.at}</span>
                    <span className={`cmt-state ${c.state}`}>{c.state}</span>
                  </div>
                  <div className="cmt-body">{c.body}</div>
                  {(c.links || []).length > 0 && (
                    <div className="cmt-links" data-testid={`ws-${ws.n}-cmt-${ci}-links`}>
                      {(c.links || []).map((l) => (
                        <a
                          key={l.id || l.url}
                          href={l.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="cmt-link-chip"
                        >
                          🔗 {l.url}
                        </a>
                      ))}
                    </div>
                  )}
                  <AttachmentList
                    items={c.attachments || []}
                    canDelete={isAdmin}
                    onDelete={(ai) => delCommentAttachment(ci, ai)}
                    testIdPrefix={`ws-${ws.n}-cmt-${ci}-att`}
                  />
                  {isAdmin && (
                    <div className="cmt-actions">
                      {c.state !== "acknowledged" && (
                        <button
                          onClick={() => setCmtState(ci, "acknowledged")}
                          data-testid={`ws-${ws.n}-cmt-${ci}-ack`}
                        >
                          Mark acknowledged
                        </button>
                      )}
                      {c.state !== "resolved" && (
                        <button
                          onClick={() => setCmtState(ci, "resolved")}
                          data-testid={`ws-${ws.n}-cmt-${ci}-resolve`}
                        >
                          Mark resolved
                        </button>
                      )}
                      {c.state !== "new" && (
                        <button
                          onClick={() => setCmtState(ci, "new")}
                          data-testid={`ws-${ws.n}-cmt-${ci}-reopen`}
                        >
                          Reopen
                        </button>
                      )}
                      <button
                        onClick={() => delCmt(ci)}
                        data-testid={`ws-${ws.n}-cmt-${ci}-delete`}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
